import React, { useEffect, useState } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { PageHeader, StatusBadge, EmptyState } from '../../components/common/PageHeader';
import api from '../../lib/api';
import { Link } from 'react-router-dom';
import { Search, Briefcase, Inbox } from 'lucide-react';
import { JOB_STATUSES, statusLabel, serviceTypeLabel } from '../../lib/constants';

const AdminJobs = () => {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [interpreters, setInterpreters] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const load = async () => {
    const [j, c, i] = await Promise.all([api.get('/jobs'), api.get('/customers'), api.get('/interpreters')]);
    setJobs(j.data); setCustomers(c.data); setInterpreters(i.data);
  };
  useEffect(() => { load(); }, []);

  const customerName = (id) => customers.find(c => c.id === id)?.organization_name || '-';
  const interpreterName = (id) => {
    const i = interpreters.find(x => x.id === id);
    return i ? `${i.first_name} ${i.last_name}` : '-';
  };

  const filtered = jobs.filter(j => {
    if (statusFilter && j.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return j.id.includes(q) || j.source_language.toLowerCase().includes(q) ||
        j.target_language.toLowerCase().includes(q) || customerName(j.customer_id).toLowerCase().includes(q);
    }
    return true;
  });

  const newRequests = jobs.filter(j => j.status === 'neu').length;

  return (
    <AppLayout>
      <PageHeader title="Aufträge" description="Kundenanfragen prüfen, Gegenvorschläge machen und Dolmetscher zuweisen.">
        {newRequests > 0 && (
          <div data-testid="new-requests-badge" className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1.5 rounded-md text-sm font-medium">
            <Inbox className="w-4 h-4" /> {newRequests} neue Kundenanfrage{newRequests !== 1 ? 'n' : ''}
          </div>
        )}
      </PageHeader>

      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Suche Auftrag, Sprache, Kunde..."
            data-testid="jobs-search"
            className="w-full border border-slate-300 rounded-md pl-9 pr-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="jobs-status-filter" className="border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none">
          <option value="">Alle Status</option>
          {JOB_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={Briefcase} title="Keine Aufträge gefunden" description="Sobald Kunden Anfragen stellen, erscheinen sie hier." />
      ) : (
        <div className="bg-white border border-slate-200 rounded-md card-subtle overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Kunde</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Sprache</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Einsatz</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Datum</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Dolmetscher</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Preis</th>
                <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 py-3 px-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(j => (
                <tr key={j.id} className="interactive-row">
                  <td className="border-b border-slate-100 py-3 px-4">
                    <Link to={`/admin/jobs/${j.id}`} className="text-sm font-medium text-slate-900 hover:text-[#0F4C81]" data-testid={`job-row-${j.id}`}>{customerName(j.customer_id)}</Link>
                    <div className="text-xs text-slate-400 font-mono">{j.job_number || j.id.slice(0, 12)}</div>
                  </td>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">{j.source_language} → {j.target_language}</td>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">{serviceTypeLabel(j.service_type)}</td>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">{j.date} {j.time}</td>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700">{interpreterName(j.interpreter_id)}</td>
                  <td className="border-b border-slate-100 py-3 px-4 text-sm text-slate-700 font-mono">{j.customer_price?.toFixed(2)} €</td>
                  <td className="border-b border-slate-100 py-3 px-4"><StatusBadge status={j.status} label={statusLabel(j.status)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default AdminJobs;
